// HistoryEntry — Jira-style audit-trail row.
// Renders "{User} changed the {Field}" with a date below, then the from→to
// pair as visual pills (or avatar pair for assignee changes). Reuses across
// TaskSheet and TaskDetailPage.
import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { ArrowRight } from '@phosphor-icons/react';
import { getAvatarStyle, getInitials } from '../lib/avatarColor';

const STATUS_LABELS = { todo: 'To Do', in_progress: 'In Progress', review: 'Review', done: 'Done' };
const PRIORITY_LABELS = { low: 'Low', medium: 'Medium', high: 'High', critical: 'Critical' };
const FIELD_LABELS = {
  status: 'Status', priority: 'Priority', assignee: 'Assignee',
  title: 'Title', work_type: 'Type', description: 'Description',
  due_date: 'Due date', sprint: 'Sprint', sprint_id: 'Sprint',
  tags: 'Labels',
  original_estimate_hours: 'Original estimate',
  remaining_hours: 'Remaining estimate',
  time_spent_hours: 'Time spent',
  custom_field: 'Field',
};

const Pill = ({ children, color = 'zinc' }) => {
  const palette = {
    zinc: 'bg-zinc-100 text-zinc-700 border-zinc-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10.5px] font-bold uppercase tracking-[0.04em] ${palette[color] || palette.zinc}`}>{children}</span>
  );
};

const STATUS_COLOR = { todo: 'zinc', in_progress: 'blue', review: 'amber', done: 'emerald' };
const PRIORITY_COLOR = { low: 'zinc', medium: 'amber', high: 'red', critical: 'red' };

const fmtDate = (d) => {
  if (!d) return '';
  try {
    const date = new Date(d);
    const today = new Date();
    const diffMs = today - date;
    const day = 24 * 60 * 60 * 1000;
    if (diffMs < day && date.getDate() === today.getDate()) {
      return 'today, ' + date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }
    if (diffMs < 2 * day) return 'yesterday';
    if (diffMs < 7 * day) return Math.floor(diffMs / day) + ' days ago';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return ''; }
};

// Decode the common HTML entities that leak in from rich-text fields so the
// audit feed never shows raw "&amp;" / "&lt;" etc.
const decodeEntities = (s) => String(s)
  .replace(/&nbsp;/g, ' ')
  .replace(/&amp;/g, '&')
  .replace(/&lt;/g, '<')
  .replace(/&gt;/g, '>')
  .replace(/&quot;/g, '"')
  .replace(/&#0?39;|&apos;/g, "'")
  .replace(/&#x([0-9a-fA-F]+);/g, (_, h) => { try { return String.fromCodePoint(parseInt(h, 16)); } catch { return _; } })
  .replace(/&#(\d+);/g, (_, d) => { try { return String.fromCodePoint(parseInt(d, 10)); } catch { return _; } });

// Strip HTML tags, decode entities & collapse whitespace — we never want raw
// <p>… markup or &amp; leaking into the audit display.
const stripHtml = (s) => {
  if (s == null) return '';
  return decodeEntities(String(s).replace(/<[^>]*>/g, ' ')).replace(/\s+/g, ' ').trim();
};

// Build a snippet of `full` centred on where it first diverges from `other`,
// so the actual change is visible even when both strings share a long prefix.
const changeSnippet = (full, other, before = 30, after = 80) => {
  const a = full || '', b = other || '';
  if (!a) return '';
  const min = Math.min(a.length, b.length);
  let start = 0;
  while (start < min && a[start] === b[start]) start++;
  const from = Math.max(0, start - before);
  const to = Math.min(a.length, start + after);
  let out = a.slice(from, to);
  if (from > 0) out = '…' + out;
  if (to < a.length) out = out + '…';
  return out;
};

// Long-text before→after — stacked, colour-coded, and focused on the change.
const TextDiff = ({ from, to }) => {
  const f = stripHtml(from), t = stripHtml(to);
  return (
    <div className="mt-1.5 ml-9 space-y-1 max-w-full" data-testid="history-text-diff">
      <div className="flex items-start gap-2">
        <span className="text-[9.5px] font-bold uppercase tracking-wider text-zinc-400 w-8 shrink-0 pt-1">From</span>
        {f
          ? <span className="text-[12px] text-red-700/90 bg-red-50 border border-red-100 rounded px-1.5 py-0.5 [overflow-wrap:anywhere] line-through decoration-red-300">{changeSnippet(f, t)}</span>
          : <span className="text-[11px] italic text-zinc-400 pt-0.5">None</span>}
      </div>
      <div className="flex items-start gap-2">
        <span className="text-[9.5px] font-bold uppercase tracking-wider text-zinc-400 w-8 shrink-0 pt-1">To</span>
        {t
          ? <span className="text-[12px] text-emerald-800 bg-emerald-50 border border-emerald-100 rounded px-1.5 py-0.5 [overflow-wrap:anywhere]">{changeSnippet(t, f)}</span>
          : <span className="text-[11px] italic text-zinc-400 pt-0.5">None</span>}
      </div>
    </div>
  );
};

// Fields whose values are free text / rich text — rendered as a stacked diff.
const LONG_TEXT_FIELDS = new Set(['description', 'title', 'custom_field']);
const isLongText = (field, from, to) =>
  LONG_TEXT_FIELDS.has(field) ||
  (typeof from === 'string' && from.length > 40) ||
  (typeof to === 'string' && to.length > 40);

// Display value formatter — produces JSX given (field, raw value).
const renderValue = (field, value, members) => {
  if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
    return <span className="text-[11px] italic text-zinc-400">None</span>;
  }
  if (field === 'status') return <Pill color={STATUS_COLOR[value] || 'zinc'}>{STATUS_LABELS[value] || value}</Pill>;
  if (field === 'priority') return <Pill color={PRIORITY_COLOR[value] || 'zinc'}>{PRIORITY_LABELS[value] || value}</Pill>;
  if (field === 'assignee') {
    const m = (members || []).find(u => u.user_id === value);
    if (!m) return <Pill color="zinc">{value}</Pill>;
    const s = getAvatarStyle(m.user_id || m.name);
    return (
      <span className="inline-flex items-center gap-1.5">
        <Avatar className="h-5 w-5"><AvatarImage src={m.avatar} /><AvatarFallback className={`${s.bg} text-white text-[8px] font-bold`}>{getInitials(m.name)}</AvatarFallback></Avatar>
        <span className="text-[12px] text-zinc-700">{m.name}</span>
      </span>
    );
  }
  if (field === 'work_type') return <Pill color="indigo">{String(value).replace(/_/g, ' ')}</Pill>;
  if (field === 'due_date') {
    try { return <Pill color="zinc">{new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</Pill>; }
    catch { return <Pill color="zinc">{String(value)}</Pill>; }
  }
  if (Array.isArray(value)) return <Pill color="zinc">{value.join(', ') || '—'}</Pill>;
  // Long text fields (description, custom rich-text fields). Strip HTML and
  // truncate to a snippet — full diff would bloat the audit feed.
  const txt = stripHtml(value);
  return <span className="text-[12px] text-zinc-600 break-words">{txt.length > 100 ? txt.slice(0, 100) + '…' : (txt || String(value).slice(0, 100))}</span>;
};

export default function HistoryEntry({ activity, members, getMemberAvatar, getMemberName }) {
  const userName = getMemberName(activity.user_id) || 'Someone';
  const userAvatar = getMemberAvatar(activity.user_id);
  const aStyle = getAvatarStyle(activity.user_id || userName);

  let headline;
  let body = null;

  if (activity.action === 'created') {
    headline = <><strong className="font-bold text-zinc-900">{userName}</strong> <span className="text-zinc-500">created this issue</span></>;
  } else if (activity.action === 'commented') {
    headline = <><strong className="font-bold text-zinc-900">{userName}</strong> <span className="text-zinc-500">added a comment</span></>;
  } else if (activity.action === 'comment_edited') {
    headline = <><strong className="font-bold text-zinc-900">{userName}</strong> <span className="text-zinc-500">edited a comment</span></>;
  } else if (activity.action === 'comment_deleted') {
    headline = <><strong className="font-bold text-zinc-900">{userName}</strong> <span className="text-zinc-500">deleted a comment</span></>;
  } else if (activity.action === 'updated' && activity.details) {
    const field = activity.details.field;
    // Special non-scalar fields (attachment / link / watchers) carry
    // their own sub-action inside `details.action` and never have a
    // from→to pair. Render them as a single-line headline.
    if (field === 'attachment') {
      const verb = activity.details.action === 'removed' ? 'removed' : 'added';
      headline = <><strong className="font-bold text-zinc-900">{userName}</strong> <span className="text-zinc-500">{verb} attachment</span> <strong className="font-semibold text-zinc-700 break-all">{activity.details.filename || 'file'}</strong></>;
    } else if (field === 'link') {
      const verb = activity.details.action === 'removed' ? 'removed' : 'added';
      const lt = (activity.details.link_type || 'relates_to').replace(/_/g, ' ');
      headline = <><strong className="font-bold text-zinc-900">{userName}</strong> <span className="text-zinc-500">{verb} link</span> <strong className="font-semibold text-zinc-700">{lt}</strong> <span className="text-zinc-500">→</span> <strong className="font-mono text-[12px] text-zinc-700">{activity.details.target_task_id?.slice?.(-8) || activity.details.target_task_id}</strong></>;
    } else if (field === 'watchers') {
      const verb = activity.details.action === 'unsubscribed' ? 'stopped watching' : 'started watching';
      headline = <><strong className="font-bold text-zinc-900">{userName}</strong> <span className="text-zinc-500">{verb} this issue</span></>;
    } else {
      // Custom fields carry their friendly label on the activity row itself —
      // prefer that over the generic "Field" label.
      const fieldLabel = field === 'custom_field'
        ? (activity.details.field_label || 'Field')
        : (FIELD_LABELS[field] || field?.replace(/_/g, ' '));
      headline = <><strong className="font-bold text-zinc-900">{userName}</strong> <span className="text-zinc-500">changed the</span> <strong className="font-semibold text-zinc-700">{fieldLabel}</strong></>;
      body = isLongText(field, activity.details.from, activity.details.to)
        ? <TextDiff from={activity.details.from} to={activity.details.to} />
        : (
          <div className="flex items-center flex-wrap gap-2 mt-1.5 ml-9">
            {renderValue(field, activity.details.from, members)}
            <ArrowRight size={12} className="text-zinc-400 flex-shrink-0" />
            {renderValue(field, activity.details.to, members)}
          </div>
        );
    }
  } else {
    headline = <><strong className="font-bold text-zinc-900">{userName}</strong> <span className="text-zinc-500">{activity.action}</span></>;
  }

  return (
    <div className="flex gap-2.5 items-start py-3" data-testid={`history-entry-${activity.activity_id}`}>
      <Avatar className="h-7 w-7 flex-shrink-0">
        <AvatarImage src={userAvatar} />
        <AvatarFallback className={`${aStyle.bg} text-white text-[10px] font-bold`}>{getInitials(userName)}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] text-zinc-700 leading-snug">{headline}</p>
        <p className="text-[10.5px] text-zinc-400 mt-0.5">{fmtDate(activity.created_at)}</p>
        {body}
      </div>
    </div>
  );
}
